import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChecklistIcon from "@mui/icons-material/PlaylistAddCheck";

import { findByTdpCodigoRegistered } from "./api-cronograma-quant-aula-bimestre";
import { errorMessage } from "../../../util/ConfirmAlertUtil";

/**
 * Calcula o percentual de aulas já registradas em relação ao total planejado.
 */
function getProgress(quantAulas, quantAulasRegistradas) {
  if (!quantAulas) return 0;
  return Math.min(100, Math.round((quantAulasRegistradas / quantAulas) * 100));
}

/**
 * Retorna a cor do indicador de progresso conforme o percentual concluído.
 */
function getProgressColor(progress) {
  if (progress >= 100) return "success";
  if (progress >= 50) return "warning";
  return "error";
}

function BimestreCard({ tdp, cqab }) {
  const progress = getProgress(cqab.quantAulas, cqab.quantAulasRegistradas);
  const color = getProgressColor(progress);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {cqab.bimestre.numeroBimestre}º Bimestre
        </Typography>
        <Chip
          label={`${progress}%`}
          color={color}
          size="small"
          variant={progress >= 100 ? "filled" : "outlined"}
        />
      </Box>

      <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
        {cqab.quantAulasRegistradas}
        <Typography component="span" variant="body2" color="text.secondary">
          {" "}
          / {cqab.quantAulas} aulas
        </Typography>
      </Typography>

      <LinearProgress
        variant="determinate"
        value={progress}
        color={color}
        sx={{ borderRadius: 1, height: 6 }}
      />

      <Box sx={{ mt: "auto", pt: 1 }}>
        <Link
          to={{ pathname: "/conferir-cronograma-presenca" }}
          state={{ tdp, bimestre: cqab.bimestre }}
          style={{ textDecoration: "none" }}
        >
          <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
            Conferir →
          </Typography>
        </Link>
      </Box>
    </Paper>
  );
}

BimestreCard.propTypes = {
  tdp: PropTypes.object.isRequired,
  cqab: PropTypes.shape({
    quantAulas: PropTypes.number,
    quantAulasRegistradas: PropTypes.number,
    bimestre: PropTypes.shape({
      numeroBimestre: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
  }).isRequired,
};

export default function PlanejamentoQuantAulasReg({ tdp }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    if (!tdp?.codigo) return;

    setLoading(true);
    findByTdpCodigoRegistered(tdp.codigo)
      .then((data) => {
        if (data.error) {
          errorMessage(data.message);
          setList([]);
        } else {
          setList(data);
        }
      })
      .catch(() => {
        errorMessage("Não foi possível carregar o planejamento de aulas.");
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [tdp]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedList = useMemo(
    () =>
      [...list].sort(
        (a, b) => a.bimestre.numeroBimestre - b.bimestre.numeroBimestre,
      ),
    [list],
  );

  if (!loading && sortedList.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ mt: 2.5, width: "100%", borderRadius: 2 }}>
      <CardHeader
        avatar={<ChecklistIcon color="action" />}
        title="Planejamento de Aulas Registradas"
        subheader="Quantidade de aulas planejadas vs. registradas por bimestre"
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
        subheaderTypographyProps={{ variant: "body2" }}
        action={
          <Tooltip title="Atualizar">
            <span>
              <IconButton
                onClick={loadData}
                disabled={loading}
                aria-label="Atualizar planejamento de aulas"
                size="small"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {sortedList.map((cqab) => (
              <Grid
                key={cqab.bimestre.numeroBimestre}
                size={{ xs: 12, sm: 6, md: 3 }}
              >
                <BimestreCard tdp={tdp} cqab={cqab} />
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

PlanejamentoQuantAulasReg.propTypes = {
  tdp: PropTypes.shape({
    codigo: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
  }).isRequired,
};
